const ACTIVE_KEY = 'ar_web_push_active';

const urlBase64ToUint8Array = (value) => {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

export const webPushSupported = () => (
  typeof window !== 'undefined'
  && 'Notification' in window
  && 'serviceWorker' in navigator
  && 'PushManager' in window
);

export const isWebPushActive = () => window.localStorage.getItem(ACTIVE_KEY) === 'true';

const deviceName = () => {
  const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches;
  const platform = navigator.userAgentData?.platform || navigator.platform || 'Dispositivo';
  return `${standalone ? 'PWA' : 'Navegador'} · ${platform}`.slice(0, 160);
};

const persistSubscription = async (api, subscription) => {
  const value = subscription.toJSON();
  await api.post('/push/subscriptions', {
    endpoint: value.endpoint,
    keys: value.keys,
    app_scope: 'both',
    device_name: deviceName(),
  });
  window.localStorage.setItem(ACTIVE_KEY, 'true');
  return subscription;
};

export const enableWebPush = async (api) => {
  if (!webPushSupported()) throw new Error('Este dispositivo no admite notificaciones PWA.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Debes permitir las notificaciones en la configuración del sitio.');
  const [{ data }, registration] = await Promise.all([
    api.get('/push/public-key'),
    navigator.serviceWorker.ready,
  ]);
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  return persistSubscription(api, subscription);
};

export const syncWebPush = async (api) => {
  if (!webPushSupported() || Notification.permission !== 'granted') return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    window.localStorage.removeItem(ACTIVE_KEY);
    return false;
  }
  await persistSubscription(api, subscription);
  return true;
};

export const disableWebPush = async (api) => {
  if (!webPushSupported()) return;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await api.delete('/push/subscriptions', { data: { endpoint: subscription.endpoint } }).catch(() => {});
    await subscription.unsubscribe();
  }
  window.localStorage.removeItem(ACTIVE_KEY);
};

const residentPushBase = ({ slug, publicToken, residentToken }) => (
  `/api/push/resident/${encodeURIComponent(slug)}/${encodeURIComponent(publicToken)}/${encodeURIComponent(residentToken)}`
);

const persistResidentSubscription = async (identity, subscription) => {
  const value = subscription.toJSON();
  const response = await fetch(`${residentPushBase(identity)}/subscriptions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint: value.endpoint, keys: value.keys, app_scope: 'ares', device_name: deviceName() }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data?.detail || 'No fue posible vincular este dispositivo.');
  }
  window.localStorage.setItem(ACTIVE_KEY, 'true');
  return subscription;
};

export const enableResidentWebPush = async (identity) => {
  if (!webPushSupported()) throw new Error('Este dispositivo no admite notificaciones PWA.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Debes permitir las notificaciones en la configuración del sitio.');
  const [keyResponse, registration] = await Promise.all([
    fetch('/api/push/resident/public-key'),
    navigator.serviceWorker.ready,
  ]);
  if (!keyResponse.ok) throw new Error('Los avisos del dispositivo no están disponibles por el momento.');
  const { public_key: publicKey } = await keyResponse.json();
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  return persistResidentSubscription(identity, subscription);
};

export const syncResidentWebPush = async (identity) => {
  if (!webPushSupported() || Notification.permission !== 'granted') return false;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return false;
  await persistResidentSubscription(identity, subscription);
  return true;
};
